import React, { useState } from "react";
import { Badge, DataTable, Pagination, Button } from "../../../components/ui";
import { fmtDate } from "../../../lib/formatters";
import { Eye, Truck, Plus, Check, X } from "lucide-react";
import { getCustomerDisplayId } from "../../../constants/globalConstants";

export const MaterialRequestsTable = ({ activeTab, materialRequests, projects = [], user, onViewRequest, onDeliver, onMarkAsDelivered, onOwnerAction, onAddItems }) => {
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const isInventoryManager =
    user?.role === "inventory_manager" ||
    user?.role === "store-manager" ||
    user?.role === "admin" ||
    user?.role === "solar-admin" ||
    user?.role === "owner";

  const isOwnerOrAdmin =
    user?.role === "owner" ||
    user?.role === "admin" ||
    user?.role === "solar-admin";

  const hasEnoughStock = (req) => {
    const items = req?.items || [];
    return items.every((it) => {
      const availableQty = Number(it?.item?.availableQty || 0);
      const neededQty = Number(it?.qty || 0) - Number(it?.deliveredQty || 0);
      return availableQty >= Math.max(0, neededQty);
    });
  };

  const columns = [
    {
      header: "Customer",
      render: (r) => {
        const matchedProj = Array.isArray(projects) ? projects.find(p => 
          (p._id?.toString() === r.projectId?.toString()) || 
          (p.id?.toString() === r.projectId?.toString()) || 
          (p.projectId === r.projectId)
        ) : null;
        const proj = matchedProj || r.projectDetails;
        const name = proj?.customerName || r.projectDetails?.customerName || "Project";
        return (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center text-slate-500 font-medium text-[12px] border border-slate-200">
              {name.charAt(0)}
            </div>
            <span className="font-semibold text-slate-800 text-[12px] truncate block max-w-[200px]">
              {name}
            </span>
          </div>
        );
      }
    },
    {
      header: "Project ID",
      render: (r) => {
        const matchedProj = Array.isArray(projects) ? projects.find(p => 
          (p._id?.toString() === r.projectId?.toString()) || 
          (p.id?.toString() === r.projectId?.toString()) || 
          (p.projectId === r.projectId)
        ) : null;
        const proj = matchedProj || r.projectDetails;
        const customerId = getCustomerDisplayId(proj);
        return (
          <span className="text-[11px] font-bold text-slate-700 tracking-wider uppercase">
            {customerId || "—"}
          </span>
        );
      }
    },
    {
      header: "Items",
      className: "text-center",
      render: (r) => (
        <span className="text-[12px] font-medium text-slate-600">
          {r.items?.length || 0} Items
        </span>
      )
    },
    {
      header: "Status",
      render: (r) => (
        <Badge 
          status={r.status} 
          label={r.status === 'owner_approved' && (r.approvedBy?.role === 'admin' || r.approvedBy?.role === 'solar-admin') ? 'Admin Approved' : undefined}
        />
      )
    },
    {
      header: "Requested Date",
      render: (r) => (
        <span className="text-[12px] text-slate-500 font-medium">
          {fmtDate(r.createdAt)}
        </span>
      )
    },
    {
      header: "Actions",
      className: "text-center w-[120px]",
      render: (r) => (
        <div className="flex items-center justify-start gap-1 w-[80px] mx-auto">
          <Button 
            variant="ghost" 
            size="icon-xs" 
            className="text-slate-400 hover:text-[#2b448f] hover:bg-slate-50"
            onClick={() => onViewRequest(r)} 
            title="View Details"
          >
            <Eye size={14} />
          </Button>

          {String(r.status || '').toLowerCase() === 'pending' && (
            <>
              <Button 
                type="button"
                variant="ghost" 
                size="icon-xs"
                onClick={() => onOwnerAction(r._id || r.id, 'AWAITING_OWNER_APPROVAL')} 
                className="text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50"
                title="Accept & Send to Owner"
              >
                <Check size={14} />
              </Button>
              <Button 
                type="button"
                variant="ghost" 
                size="icon-xs"
                onClick={() => onOwnerAction(r._id || r.id, 'rejected')} 
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                title="Reject"
              >
                <X size={14} />
              </Button>
            </>
          )}

          {String(r.status || '').toLowerCase() === 'awaiting_owner_approval' && isOwnerOrAdmin && (
            <>
              <Button 
                type="button"
                variant="ghost" 
                size="icon-xs"
                onClick={() => onOwnerAction(r._id || r.id, 'owner_approved')} 
                className="text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50"
                title="Approve Request"
              >
                <Check size={14} />
              </Button>
              <Button 
                type="button"
                variant="ghost" 
                size="icon-xs"
                onClick={() => onOwnerAction(r._id || r.id, 'rejected')} 
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                title="Reject Request"
              >
                <X size={14} />
              </Button>
            </>
          )}

          {isInventoryManager && ['approved', 'partially_delivered'].includes(String(r.status || '').toLowerCase()) && (
            <Button 
              variant="ghost" 
              size="icon-xs"
              onClick={() => hasEnoughStock(r) ? onDeliver(r) : null}
              disabled={!hasEnoughStock(r)}
              className={
                hasEnoughStock(r)
                  ? "text-slate-400 hover:text-[#f97316] hover:bg-orange-50"
                  : "text-slate-300 cursor-not-allowed"
              }
              title="Dispatch"
            >
              <Truck size={14} />
            </Button>
          )}
        </div>
      )
    }
  ];


  const currentData = (materialRequests || []).slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalPages = Math.max(Math.ceil((materialRequests || []).length / itemsPerPage), 1);

  return (
    //updated Halimunnisa 09-06-2026
    //refresh table after any action without resetting pagination to page 1, to avoid user losing context when managing multiple pages of requests
    <div className="card rounded-none overflow-hidden shadow-sm border-slate-100">
      <DataTable 
        columns={columns} 
        data={currentData} 
        noScroll={true}
        getRowClassName={(idx) => idx % 2 === 0 ? "bg-white" : "bg-slate-50/10"}
      />
      
      {(materialRequests || []).length > itemsPerPage && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
};


